import { useState } from 'react'
import { useItem, useList } from '../api/queries'
import type { Rules, Template, TemplateVersion } from '../api/types'
import { DataTable } from '../components/DataTable'
import { BandBadge, Card, ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui'
import { humanize } from '../lib/format'
import { inspectionText } from '../features/inspections/presentation'

function VersionView({ id }: { id: number }) {
  const v = useItem<TemplateVersion>(`/api/template-versions/${id}/`)
  if (v.isPending) return <LoadingState />
  if (v.isError) return <ErrorState error={v.error} />
  const rules = v.data.rules as unknown as Rules
  return (
    <div className="stack">
      <Card title={`Criterios (${rules.criteria.length})`}>
        <DataTable caption="Criterios" rows={rules.criteria} rowKey={(c) => c.code} columns={[
          { key: 'n', header: '#', cell: (c) => c.number },
          { key: 'name', header: 'Criterio', cell: (c) => <>{inspectionText(c.name)}{c.note ? <div className="muted small">{inspectionText(c.note)}</div> : null}</> },
          { key: 'a', header: 'Normal', cell: (c) => <span className="mono">{c.values.normal}</span>, align: 'right' },
          { key: 'b', header: 'Medio', cell: (c) => <span className="mono">{c.values.medium}</span>, align: 'right' },
          { key: 'c', header: 'Crítico', cell: (c) => <span className="mono">{c.values.critical}</span>, align: 'right' },
          { key: 't', header: 'Medio / crítico significa', cell: (c) => <>{inspectionText(c.texts.medium)}<br />{inspectionText(c.texts.critical)}</>, priority: 'low' },
        ]} />
      </Card>
      <Card title="Bandas y redondeo">
        <p className="muted">Los totales se redondean (mitad hacia arriba) a {rules.rounding.places} decimal(es) antes de asignar la banda.</p>
        <ul className="threshold-list">{rules.bands.map((b) => <li key={b.code}><BandBadge band={b.code} size="sm" /> <span className="mono">{b.max === null ? `${b.min} o más` : `${b.min} – ${b.max}`}</span></li>)}</ul>
      </Card>
      {rules.motor ? (
        <Card title={<>Diagnóstico del motor <span className="tag-reference">Referencia</span></>}>
          <p className="muted">{inspectionText(rules.motor.note ?? 'Solo referencia.')} Rangos tomados de las fichas de los equipos; nunca se usan para la banda oficial, los conteos ni las alertas.</p>
          <ul className="plain">{rules.motor.bands.map((b) => <li key={b.code}><strong>{inspectionText(b.label)}</strong> <span className="mono">{b.display ?? `${b.min} – ${b.max}`}</span></li>)}</ul>
          <p>Criterios: {rules.motor.criteria.map((c) => inspectionText(rules.criteria.find((x) => x.code === c)?.name ?? c)).join(', ')}.</p>
        </Card>
      ) : null}
    </div>
  )
}

export function TemplatesPage() {
  const list = useList<Template>('/api/templates/')
  const [version, setVersion] = useState<number | null>(null)
  return (
    <div className="page">
      <PageHeader title="Plantillas" subtitle="Las versiones publicadas son inmutables; cada inspección conserva una copia congelada de sus reglas" />
      {list.isPending ? <LoadingState /> : list.isError ? <ErrorState error={list.error} /> : list.data.results.map((t) => (
        <Card key={t.id} title={<span className="mono">{t.code}</span>}>
          <p>{inspectionText(t.name)}</p>
          <div className="pills">{t.versions.map((v) => <button key={v.id} type="button" className={version === v.id ? 'pill pill--active' : 'pill'} aria-pressed={version === v.id} onClick={() => setVersion(v.id)}>Versión {v.number} <StatusBadge tone={v.status === 'published' ? 'ok' : 'neutral'} label={humanize(v.status)} /></button>)}</div>
        </Card>
      ))}
      {version ? <VersionView id={version} /> : <p className="muted">Elegir una versión para ver sus criterios.</p>}
    </div>
  )
}
